// zodCustom.ts
import { z } from 'zod';
import { startOfDay } from 'date-fns';
import { Currency } from 'core/utils';
import { Res } from 'core/api';
import { maxDecimalPlaces } from 'core/ui/controls/Constraints';

type DecimalOptions = {
	decimalPlaces?: number;
	nonNegative?: boolean;
	strictPositive?: boolean;
	required?: boolean;
	messageInvalid?: string;
	messageNonNegative?: string;
	messageDecimal?: (dp: number) => string;
};

export const zDecimal = (options: DecimalOptions = {}) => {
	const {
		decimalPlaces = 2,
		nonNegative = false,
		strictPositive = false,
		required = false,
		messageInvalid = Res.Msg.MSG_INVALID_VALUE(),
		messageNonNegative = Res.Msg.MSG_VALUE_MUST_BE_GREATER_THAN_ZERO(),
		messageDecimal = Res.Msg.MSG_INVALID_NUMBER_OF_DECIMAL_PLACES,
	} = options;

	const base = z
		.string()
		.refine(val => {
			if (required) return val.trim() !== '';
			return true;
		}, { message: Res.Msg.MSG_VALUE_REQUIRED() })
		.or(z.number())
		.transform(val => typeof val === 'string' ? Number(val) : val)
		.refine(val => !isNaN(val), { message: messageInvalid });

	return base
		.refine(val => {
			if (strictPositive) return val > 0;
			if (nonNegative) return val >= 0;
			return true;
		}, { message: messageNonNegative })
		.refine(val => {
			return maxDecimalPlaces(decimalPlaces)(val) === '';
		}, {
			message: messageDecimal(decimalPlaces),
		});
};

type QuantityOptions = DecimalOptions;

export const zQuantity = (options: QuantityOptions = {}) => {
	return zDecimal(options).refine(val => val !== 0, {
		message: Res.Msg.MSG_VALUE_CANNOT_BE_ZERO(),
	});
};

type MonetaryOptions = DecimalOptions;

export const zMonetary = (options: MonetaryOptions = { decimalPlaces: Currency.DECIMAL_PLACES }) => {
	return zDecimal(options);
};

type IntOptions = {
	nonNegative?: boolean;
	strictPositive?: boolean;
	messageInvalid?: string;
	messageNonNegative?: string;
};

export const zInt = (options: IntOptions = {}) => {
	const {
		nonNegative = false,
		strictPositive = false,
		messageInvalid = Res.Msg.MSG_INVALID_VALUE(),
		messageNonNegative = Res.Msg.MSG_VALUE_MUST_BE_GREATER_THAN_ZERO(),
	} = options;

	const base = z.coerce.number({ message: messageInvalid }).int({ message: messageInvalid });

	return base.refine(n => {
		if (strictPositive) return n > 0;
		if (nonNegative) return n >= 0;
		return true;
	}, { message: messageNonNegative });
};

const MIN_BUSINESS_DAY = new Date(1753, 0, 1, 0, 0, 0, 0);
const MAX_BUSINESS_DAY = new Date(9999, 11, 31, 23, 59, 59, 999);

export const zBusinessDay = (message = Res.Msg.MSG_INVALID_VALUE()) =>
	z.coerce.date({ errorMap: () => ({ message }) })
		.refine(x => x >= MIN_BUSINESS_DAY && x <= MAX_BUSINESS_DAY, { 
			message: Res.Msg.MSG_BUSINESS_DAY_ENDPOINTS(MIN_BUSINESS_DAY.toLocaleDateString(), MAX_BUSINESS_DAY.toLocaleDateString()) 
		})
		.transform<Date>(x => startOfDay(x));

export const zDate = (message = Res.Msg.MSG_INVALID_VALUE()) =>
	z.coerce.date({ errorMap: () => ({ message }) });

export const zNonEmptyString = (message = Res.Msg.MSG_VALUE_REQUIRED()) =>
	z.string().nonempty(message);

export const zSpecialDateString = (message = Res.Msg.MSG_INVALID_VALUE()) =>
	z.union([z.string(), z.date(), z.null()]).transform(val => val === '' ? null : val).pipe(z.nullable(zDate(message)));

export const zDecimalToString = (options: DecimalOptions = {}) =>
	zDecimal(options).transform(num => num.toString());


